import { useCallback } from 'react';

import { useParams } from 'react-router';

import { FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { useShowError } from 'hooks/useShowError';
import { useFormMutation } from 'mutations/useFormMutation';
import { useConfigPedidoStepContext } from 'pages/compra/configuracao-pedido/contexts';
import { formatInitialCondicoesVisualizacaoData } from 'pages/compra/configuracao-pedido/hooks';
import { ConfigPedidoCondicoesVisualizacao, ConfigPedidoEstados, ConfigPedidoLojas } from 'pages/compra/configuracao-pedido/types';

export function useMutateCondicoesVisualizacao(initialValues: ConfigPedidoCondicoesVisualizacao) {
    const { setActiveStepSaved } = useConfigPedidoStepContext();
    const { mutate, isLoading } = useFormMutation();
    const { enqueueSnackbar } = useSnackbar();
    const { showError } = useShowError();
    const { id } = useParams();

    /**
     * Retorna os estados do pedido a serem enviados pra api, considerando a diferenca dos valores iniciais e atuais.
     */
    const getConfigPedidoEstados = useCallback(
        ({ tpDisponibilizacao, configPedidoEstados = [] }: ConfigPedidoCondicoesVisualizacao): ConfigPedidoEstados[] => {
            const isEstados = tpDisponibilizacao === 'estados';
            const initialIds = new Set(initialValues.configPedidoEstados.map((e) => e.estado.idEstado));
            const currentIds = new Set(configPedidoEstados.map((e) => e.estado.idEstado));
            const removed = initialValues.configPedidoEstados.filter((e) => !currentIds.has(e.estado.idEstado));

            return [
                ...configPedidoEstados
                    .filter((item) => isEstados || initialIds.has(item.estado.idEstado))
                    .map((item) => ({ ...item, fgAtivo: isEstados, configPedido: { idConfigPedido: Number(id) } })),
                ...removed.map((item) => ({ ...item, fgAtivo: false, configPedido: { idConfigPedido: Number(id) } })),
            ];
        },
        [id, initialValues.configPedidoEstados],
    );

    /**
     * Retorna as lojas do pedido a serem enviados pra api, considerando a diferenca dos valores iniciais e atuais.
     */
    const getConfigPedidoLojas = useCallback(
        ({ tpDisponibilizacao, configPedidoLojas = [] }: ConfigPedidoCondicoesVisualizacao): ConfigPedidoLojas[] => {
            const isLojas = tpDisponibilizacao === 'lojas';
            const initialIds = new Set(initialValues.configPedidoLojas.map((l) => l.loja.idLoja));
            const currentIds = new Set(configPedidoLojas.map((l) => l.loja.idLoja));
            const removed = initialValues.configPedidoLojas.filter((l) => !currentIds.has(l.loja.idLoja));

            return [
                ...configPedidoLojas
                    .filter((item) => isLojas || initialIds.has(item.loja.idLoja))
                    .map((item) => ({
                        ...item,
                        fgAtivo: isLojas,
                        configPedido: { idConfigPedido: Number(id) },
                    })),
                ...removed.map((item) => ({
                    ...item,
                    fgAtivo: false,
                    configPedido: { idConfigPedido: Number(id) },
                })),
            ];
        },
        [id, initialValues.configPedidoLojas],
    );

    const onSubmit = (values: ConfigPedidoCondicoesVisualizacao, formik: FormikHelpers<ConfigPedidoCondicoesVisualizacao>) => {
        const configPedidoLojas = getConfigPedidoLojas(values);
        const configPedidoEstados = getConfigPedidoEstados(values);

        const formData: ConfigPedidoCondicoesVisualizacao = {
            ...values,
            idConfigPedido: Number(id),
            configPedidoLojas,
            configPedidoEstados,
            configPedidoFaixaRoyalties: values.fgLojaFaixaRoyalties ? values.configPedidoFaixaRoyalties : null,
        };

        mutate(
            {
                formik,
                url: '/gestao/configpedidocondicaovisualizacao',
                formData,
                preventSnack: true,
            },
            {
                onSuccess: ({ data }: any) => {
                    enqueueSnackbar('Condições de Visualização salvo com sucesso', { variant: 'success' });

                    setActiveStepSaved();

                    const configPedidoEstadosAtivos = data.data.configPedidoEstados.filter((item: ConfigPedidoEstados) => item.fgAtivo);
                    const configPedidoLojasAtivas = data.data.configPedidoLojas.filter((item: ConfigPedidoLojas) => item.fgAtivo);

                    formik.resetForm({
                        values: formatInitialCondicoesVisualizacaoData({
                            ...data.data,
                            configPedidoEstados: configPedidoEstadosAtivos,
                            configPedidoLojas: configPedidoLojasAtivas,
                        }),
                    });
                },
                onError: (error: unknown) => showError(error, 'Erro ao salvar Condições de Visualização'),
            },
        );
    };

    return { onSubmit, isLoading };
}
